import { useState, useRef, useEffect } from 'react'
import { Settings, Sliders, Download, Upload } from 'lucide-react'
import { useEnergyStore } from '@/stores/useEnergyStore'

export const SettingsDropdown = () => {
  const darkMode = useEnergyStore((s) => s.darkMode)
  const setSettingsModalOpen = useEnergyStore((s) => s.setSettingsModalOpen)
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-2 rounded-xl transition-all border cursor-pointer ${
          isOpen
            ? darkMode
              ? 'bg-white/10 border-white/10 text-white'
              : 'bg-slate-100 border-slate-300 text-slate-900'
            : darkMode
              ? 'bg-white/5 border-white/5 text-slate-400 hover:text-white hover:border-white/10'
              : 'bg-white border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-300'
        }`}
      >
        <Settings className={`w-4 h-4 transition-transform duration-500 ${isOpen ? 'rotate-45' : ''}`} />
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 mt-2 w-56 rounded-2xl border shadow-xl overflow-hidden animate-section z-50 ${
            darkMode
              ? 'bg-slate-800 border-white/10'
              : 'bg-white border-slate-200'
          }`}
        >
          <div
            className={`p-4 border-b ${
              darkMode ? 'border-white/5' : 'border-slate-100'
            }`}
          >
            <p className={`text-xs font-black uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              System Settings
            </p>
          </div>

          <div className="p-2">
            <button
              onClick={() => {
                setSettingsModalOpen(true)
                setIsOpen(false)
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
                darkMode
                  ? 'text-slate-300 hover:bg-white/5'
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Sliders className="w-3.5 h-3.5" />
              Dashboard Complexity
            </button>

            <button
              onClick={() => {
                useEnergyStore.getState().exportData()
                setIsOpen(false)
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
                darkMode
                  ? 'text-slate-300 hover:bg-white/5'
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Download className="w-3.5 h-3.5" />
              Backup Data (JSON)
            </button>

            <button
              onClick={() => {
                const input = document.createElement('input')
                input.type = 'file'
                input.accept = '.json'
                input.onchange = (e: any) => {
                  const file = e.target.files[0]
                  if (!file) return
                  const reader = new FileReader()
                  reader.onload = (re) => {
                    try {
                      const data = JSON.parse(re.target?.result as string)
                      useEnergyStore.getState().openConfirm({
                        title: 'Restore Data',
                        message: 'This will replace all current plans, configs, and portfolios with the backup. Continue?',
                        confirmText: 'Replace',
                        isDestructive: true,
                        onConfirm: () => {
                          useEnergyStore.getState().importData(data)
                          useEnergyStore.getState().openConfirm({
                            title: 'Restore Success',
                            message: 'Data restored. Refresh the page (F5) to ensure it loads correctly.',
                            type: 'success',
                            hideCancel: true,
                          })
                        }
                      })
                    } catch (err) {
                      useEnergyStore.getState().openConfirm({
                        title: 'Restore Failed',
                        message: 'Invalid backup file.',
                        type: 'error',
                        hideCancel: true
                      })
                    }
                  }
                  reader.readAsText(file)
                }
                input.click()
                setIsOpen(false)
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
                darkMode
                  ? 'text-slate-300 hover:bg-white/5'
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Upload className="w-3.5 h-3.5" />
              Restore Data
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
